import { toast } from "react-toastify";
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApiRequest } from "../Login/Hooks/useApiRequest";
import ServerErrorPage from "../../ServerError/ServerErrorPage";
import OrderSummary from "./component";
import OrderSummarySkeleton from "./OrderSummarySkeleton";

const OrderSummaryContainer = () => {
  const { makeApiRequest } = useApiRequest();
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [orderdetail, setOrderdetail] = useState(null);
  const [loader, setLoader]           = useState(false);
  const [serverError, setServerError] = useState(false);

  // ── Cancel order ────────────────────────────────────────────
  const [cancleflag, setCancleflag]               = useState(false);
  const [cancleOrderID, setCancleOrderID]         = useState(null);
  const [cancleErrorflag, setCancleErrorflag]     = useState(false);
  const [cancleErrorMsg, setCancleErrorMsg]       = useState("");
  const [cancleSuccessflag, setCancleSuccessflag] = useState(false);
  const [cancleSuccessMsg, setCancleSuccessMsg]   = useState("");

  // ── Auth guard ──────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("UserJWTAuth");
    if (!token) navigate("/");
  }, [navigate]);

  // ── Fetch on mount ──────────────────────────────────────────
  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // ── Grand total: offerPrice × unitCount across all line items ─
  const totalAmount =
    orderdetail?.lineItems?.reduce(
      (acc, item) => acc + item.offerPrice * item.unitCount,
      0
    ) ?? 0;

  // ── Claim a digital line item ────────────────────────────────
  // No email step (isEmailRequired: false) or already-verified email from
  // the bottom-sheet flow. On success routes to the digital details page —
  // same destination an already-CLAIMED item navigates to.
  const claimDigitalOrder = (digitalLineItemClaimId, claimOrderId, email) => {
    if (!digitalLineItemClaimId) return;

    const UserJWTAuth = localStorage.getItem("UserJWTAuth");
    const deviceId    = localStorage.getItem("uuid");

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v1/digital/claim`,
      method: "POST",
      headers: {
        Authorization: `Bearer ${UserJWTAuth}`,
        "Content-Type": "application/json",
      },
      data: {
        digitalLineItemId: digitalLineItemClaimId,
        ...(email ? { emailId: email } : {}),
      },
      handleSuccess: () => {
        navigate(`/digital_details?digitalLineItemClaimId=${digitalLineItemClaimId}&orderId=${claimOrderId}`);
      },
      handleError: (err) => {
        toast.error(
          err?.response?.data?.status?.message || "Failed to claim order",
          { position: "top-right", autoClose: 2000 }
        );
      },
    });
  };

  // ── Cancel order ────────────────────────────────────────────
  const CancleOrderset = (e, id) => {
    setCancleOrderID(id);
    setCancleflag(true);
  };

  const CancleOrder = () => {
    if (!cancleOrderID) return;
    const UserJWTAuth = localStorage.getItem("UserJWTAuth");
    setLoader(true);

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/order/${cancleOrderID}`,
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${UserJWTAuth}`,
      },
      handleSuccess: (res) => {
        setLoader(false);
        setCancleflag(false);
        if (res.data.status.success) {
          setCancleSuccessflag(true);
          setCancleSuccessMsg("Order cancelled successfully");
          fetchOrderDetails(); // refresh silently
        } else {
          setCancleErrorflag(true);
          setCancleErrorMsg(res.data.status.message || "Unable to cancel order");
        }
      },
      handleError: (err) => {
        setLoader(false);
        setCancleflag(false);
        setCancleErrorflag(true);
        setCancleErrorMsg(
          err?.response?.data?.status?.message || "Something went wrong"
        );
      },
    });
  };

  // ── Server error handler ────────────────────────────────────
  const handleServerError = useCallback((error) => {
    const statusCode = error?.response?.data?.status?.code;
    const httpStatus = error?.response?.status;

    if (statusCode === "INTERNAL_SERVER_ERROR" || httpStatus === 500 || httpStatus === 502) {
      setServerError(true);
      setLoader(false);
      return true;
    }
    return false;
  }, []);

  // ── API call ────────────────────────────────────────────────
  const fetchOrderDetails = () => {
    const UserJWTAuth = localStorage.getItem("UserJWTAuth");
    const deviceId    = localStorage.getItem("uuid");

    if (!UserJWTAuth) {
      navigate("/");
      return;
    }

    setLoader(true);

    makeApiRequest({
      url: `${process.env.REACT_APP_API_BASE_URL}/consumerbe/api/v2/order/${orderId}`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${UserJWTAuth}`,
      },
      handleSuccess: (res) => {
        setLoader(false);
        // res?.data?.data  →  the full order object
        setOrderdetail(res?.data?.data ?? null);
      },
      handleError: (err) => {
        if (handleServerError(err)) return;

        setLoader(false);
        const httpStatus = err?.response?.status;

        if (httpStatus === 401 || httpStatus === 403) {
          navigate("/login");
        } else {
          toast.error(
            err?.response?.data?.status?.message || "Something went wrong",
            { position: "top-right", autoClose: 2000 }
          );
        }
      },
    });
  };

  // ── Early returns ───────────────────────────────────────────
  if (serverError) return <ServerErrorPage />;

  return (
    <>
    {
      orderdetail?
    <OrderSummary
      orderdetail={orderdetail}
      totalAmount={totalAmount}
      loader={loader}
      claimDigitalOrder={claimDigitalOrder}
      // cancel
      cancleflag={cancleflag}
      setCancleflag={setCancleflag}
      CancleOrder={CancleOrder}
      CancleOrderset={CancleOrderset}
      cancleErrorflag={cancleErrorflag}
      setCancleErrorflag={setCancleErrorflag}
      cancleErrorMsg={cancleErrorMsg}
      cancleSuccessflag={cancleSuccessflag}
      setCancleSuccessflag={setCancleSuccessflag}
      cancleSuccessMsg={cancleSuccessMsg}
    />
    :<OrderSummarySkeleton/>
}
    </>

  );
};

export default OrderSummaryContainer;